import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, ShieldCheck, ShieldAlert, ShieldX, FileText, Check, X, Eye,
  Pause, Play, Plus, Loader2, Download, Trash2, UserPlus, Edit3, Mail,
} from "lucide-react";

const STATUS_OPTIONS = [
  "active", "approved", "awaiting_payment", "pending_verification",
  "review_required", "declined", "fraud", "risk", "suspended", "expired",
];

const ACTIONS = [
  { id: "approve", label: "Approve", icon: Check, color: "emerald" },
  { id: "decline", label: "Decline", icon: X, color: "rose" },
  { id: "review_required", label: "Need more docs", icon: FileText, color: "amber" },
  { id: "risk", label: "Mark risk", icon: ShieldAlert, color: "amber" },
  { id: "fraud", label: "Mark fraud", icon: ShieldX, color: "rose" },
  { id: "suspend", label: "Suspend", icon: Pause, color: "rose" },
  { id: "reactivate", label: "Reactivate", icon: Play, color: "emerald" },
];

const DELETABLE = new Set(["suspended", "declined", "fraud", "risk", "expired"]);

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusEdit, setStatusEdit] = useState(null); // { user_id, current }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers${filter ? `?status=${filter}` : ""}`);
      setList(data.customers);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const deleteCustomer = async (user) => {
    const status = (user.status || "").toLowerCase();
    if (!DELETABLE.has(status)) {
      toast.error("Only suspended/declined/fraud/risk/expired accounts can be deleted");
      return;
    }
    if (!window.confirm(`Delete ${user.email}? This is permanent and cascades all their data.`)) return;
    try {
      await api.delete(`/admin/customers/${user.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-root">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Customer Management
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight">All customers · {list.length}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="admin-create-customer-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-95 transition"
          >
            <UserPlus className="h-4 w-4" /> Create customer
          </button>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-customer-filter"
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="">All statuses</option>
            <option value="pending_verification">Pending verification</option>
            <option value="review_required">Review required</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
            <option value="declined">Declined</option>
            <option value="fraud">Fraud</option>
            <option value="risk">Risk</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </header>

      <div className="glass rounded-2xl overflow-hidden">
        {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/5 bg-white/[0.02]">
                <th className="py-3 px-4">Customer</th><th className="py-3 px-4">Plan</th>
                <th className="py-3 px-4">Status</th><th className="py-3 px-4">Submitted</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.map(({ user, subscription, profile }) => (
                <tr key={user.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`customer-row-${user.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-semibold">{profile?.business_name || user.name}</div>
                    <div className="text-xs text-white/40 font-mono">{user.email}</div>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {subscription?.plan_name || <span className="text-white/30">—</span>}
                    {subscription?.amount ? <div className="text-white/40 font-mono">₹{subscription.amount.toLocaleString("en-IN")}</div> : null}
                  </td>
                  <td className="py-3 px-4">
                    <StatusBadge status={subscription?.status || (user.status || "active")} />
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-white/40">
                    {subscription?.created_at ? new Date(subscription.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setSelected(user.id)} data-testid={`customer-review-${user.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg glass text-xs hover:bg-white/5">
                        <Eye className="h-3 w-3" /> Review
                      </button>
                      <button
                        onClick={() => setStatusEdit({ user_id: user.id, current: user.status || "active", email: user.email })}
                        data-testid={`customer-status-${user.id}`}
                        title="Change account status"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg glass text-xs hover:bg-white/5"
                      >
                        <Edit3 className="h-3 w-3" />
                      </button>
                      {DELETABLE.has((user.status || "").toLowerCase()) && (
                        <button
                          onClick={() => deleteCustomer(user)}
                          data-testid={`customer-delete-${user.id}`}
                          title="Delete account"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-rose-300 hover:bg-rose-500/15"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={5} className="py-12 text-center text-white/40 text-sm">No customers found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && <ReviewDrawer userId={selected} onClose={() => setSelected(null)} onUpdate={load} />}
      {createOpen && <CreateCustomerModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {statusEdit && (
        <StatusEditModal
          user={statusEdit}
          onClose={() => setStatusEdit(null)}
          onSaved={() => { setStatusEdit(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", status: "active" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/customers/create", form);
      toast.success(`Customer ${form.email} created`);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80" onClick={onClose}>
      <div className="bg-[#0c0626] border border-fuchsia-500/30 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-customer-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-black text-lg inline-flex items-center gap-2"><UserPlus className="h-5 w-5 text-fuchsia-400" /> Create customer</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          {[
            ["name", "Full name", "text"],
            ["email", "Email", "email"],
            ["phone", "Phone (optional)", "tel"],
            ["password", "Temporary password", "text"],
          ].map(([k, label, type]) => (
            <label key={k} className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">{label}</span>
              <input
                type={type}
                value={form[k]}
                onChange={set(k)}
                data-testid={`new-customer-${k}`}
                className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              />
            </label>
          ))}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Initial status</span>
            <select
              value={form.status}
              onChange={set("status")}
              data-testid="new-customer-status"
              className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </label>
        </div>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="create-customer-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-bold text-sm bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-95 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create account
        </button>
      </div>
    </div>
  );
}

function StatusEditModal({ user, onClose, onSaved }) {
  const [status, setStatus] = useState(user.current);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/customers/${user.user_id}/status`, { status, remarks: remarks || null });
      toast.success(`Status set to ${status}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80" onClick={onClose}>
      <div className="bg-[#0c0626] border border-indigo-500/30 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="status-edit-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-black text-lg inline-flex items-center gap-2"><Edit3 className="h-5 w-5 text-indigo-400" /> Set account status</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="glass rounded-lg p-3 text-xs mb-4">
          <div className="text-white/40 uppercase tracking-[0.2em] mb-1">Customer</div>
          <div className="font-mono">{user.email}</div>
          <div className="text-white/40 mt-2">Current: <span className="font-mono uppercase text-amber-300">{user.current}</span></div>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">New status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="status-edit-select"
            className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Remarks (optional)</span>
          <textarea
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            data-testid="status-edit-remarks"
            className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
            placeholder="Reason for the change…"
          />
        </label>
        <button
          onClick={save}
          disabled={busy}
          data-testid="status-edit-save"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-bold text-sm bg-gradient-to-r from-indigo-500 to-cyan-500 hover:opacity-95 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Save status
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_verification: "bg-amber-500/15 text-amber-300",
    review_required: "bg-amber-500/15 text-amber-300",
    approved: "bg-emerald-500/15 text-emerald-300",
    active: "bg-emerald-500/15 text-emerald-300",
    declined: "bg-rose-500/15 text-rose-300",
    fraud: "bg-rose-500/15 text-rose-300",
    risk: "bg-amber-500/15 text-amber-300",
    suspended: "bg-rose-500/15 text-rose-300",
    expired: "bg-white/5 text-white/40",
  };
  return <span className={`px-2 py-0.5 rounded font-mono uppercase text-[10px] ${map[status] || "bg-white/5 text-white/40"}`}>{(status || "—").replace(/_/g, " ")}</span>;
}

function ReviewDrawer({ userId, onClose, onUpdate }) {
  const [detail, setDetail] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get(`/admin/customers/${userId}`).then((r) => setDetail(r.data));
  }, [userId]);

  const act = async (action) => {
    setBusy(action);
    try {
      await api.post(`/admin/customers/${userId}/verify`, { action, remarks: remarks || null });
      toast.success(`Customer ${action}`);
      onUpdate(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  if (!detail) return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70" onClick={onClose}>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-[#0a0a13] border-l border-white/10 overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()} data-testid="customer-review-drawer">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-2xl font-black">{detail.profile?.business_name || detail.user.name}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">×</button>
        </div>

        <div className="space-y-4 text-sm">
          <Section title="Customer">
            <Row k="Email" v={detail.user.email} />
            <Row k="Phone" v={detail.user.phone || detail.profile?.phone} />
            <Row k="Business" v={detail.profile?.business_name} />
            <Row k="Category" v={detail.profile?.business_category} />
            <Row k="GST" v={detail.profile?.gst_number} />
            <Row k="PAN" v={detail.profile?.pan_number} />
            <Row k="Address" v={[detail.profile?.address, detail.profile?.city, detail.profile?.state, detail.profile?.pin_code].filter(Boolean).join(", ")} />
          </Section>

          {detail.subscription && (
            <Section title="Subscription">
              <Row k="Plan" v={detail.subscription.plan_name} />
              <Row k="Amount" v={`₹${(detail.subscription.amount || 0).toLocaleString("en-IN")}`} />
              <Row k="Method" v={detail.subscription.payment_method} />
              <Row k="Transaction ID" v={detail.subscription.transaction_id} />
              <Row k="Bank RRN" v={detail.subscription.bank_rrn} />
              <Row k="Status" v={detail.subscription.status} />
              <Row k="Submitted" v={detail.subscription.created_at ? new Date(detail.subscription.created_at).toLocaleString() : ""} />
              <Row k="Notes" v={detail.subscription.customer_notes} />
            </Section>
          )}

          {detail.documents?.length > 0 && (
            <Section title={`Documents (${detail.documents.length})`}>
              {detail.documents.map((d) => (
                <div key={d.id} className="glass rounded-lg p-2 flex items-center gap-2 text-xs">
                  <FileText className="h-3 w-3" />
                  <span className="text-white/70 flex-1 truncate">{d.filename}</span>
                  <span className="text-white/40 font-mono">{d.type}</span>
                  <span className={`px-2 py-0.5 rounded font-mono text-[10px] ${d.status === "approved" ? "bg-emerald-500/15 text-emerald-300" : d.status === "rejected" ? "bg-rose-500/15 text-rose-300" : "bg-white/5 text-white/60"}`}>{d.status}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/${d.storage_path}`} target="_blank" rel="noreferrer" className="text-indigo-300"><Download className="h-3 w-3" /></a>
                </div>
              ))}
            </Section>
          )}

          {detail.invoices?.length > 0 && (
            <Section title={`Invoices (${detail.invoices.length})`}>
              {detail.invoices.map((inv) => (
                <div key={inv.id} className="glass rounded-lg p-2 flex items-center gap-2 text-xs" data-testid={`drawer-invoice-${inv.id}`}>
                  <span className="font-mono text-indigo-300">{inv.invoice_number}</span>
                  <span className="text-white/70 flex-1 truncate">{inv.plan_name}</span>
                  <span className="font-mono">₹{(inv.amount || 0).toLocaleString("en-IN")}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" className="text-indigo-300 p-1.5 rounded hover:bg-white/5" title="Download PDF">
                    <Download className="h-3 w-3" />
                  </a>
                  <SendInvoiceEmailBtn invoiceId={inv.id} hasPayment={!!inv.paid_date || inv.payment_status === "paid"} />
                </div>
              ))}
            </Section>
          )}

          {detail.contract && (
            <Section title="Signed agreement">
              <Row k="Reference" v={detail.contract.agreement_ref} />
              <Row k="Accepted" v={detail.contract.accepted_at ? new Date(detail.contract.accepted_at).toLocaleString() : ""} />
              <Row k="IP" v={detail.contract.ip} />
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contract/${detail.contract.id}/pdf`} target="_blank" rel="noreferrer"
                data-testid="drawer-contract-pdf"
                className="inline-flex items-center gap-1 text-indigo-300 hover:underline text-xs mt-2">
                <Download className="h-3 w-3" /> Download signed PDF
              </a>
            </Section>
          )}
        </div>

        <div className="mt-6 sticky bottom-0 bg-[#0a0a13] pt-4 border-t border-white/10">
          <Section title="Admin action">
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
              placeholder="Optional remarks (visible to customer)"
              data-testid="customer-review-remarks"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mb-3" />
            <div className="grid grid-cols-2 gap-2">
              {ACTIONS.map((a) => (
                <button key={a.id} onClick={() => act(a.id)} disabled={busy === a.id}
                  data-testid={`customer-action-${a.id}`}
                  className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-${a.color}-500/10 text-${a.color}-300 hover:bg-${a.color}-500/20 disabled:opacity-50`}>
                  {busy === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <a.icon className="h-3 w-3" />}
                  {a.label}
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex gap-3 text-xs">
      <span className="text-white/40 w-28 shrink-0">{k}</span>
      <span className="text-white/80 font-mono break-all">{v}</span>
    </div>
  );
}

function SendInvoiceEmailBtn({ invoiceId, hasPayment }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/invoices/${invoiceId}/send-email`);
      toast.success(`Sent to ${data.to}${data.with_payment_proof ? " · with payment proof" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={send}
      disabled={busy}
      data-testid={`send-invoice-email-${invoiceId}`}
      title={hasPayment ? "Email invoice + payment proof to customer" : "Email invoice to customer"}
      className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300 disabled:opacity-50 inline-flex items-center gap-1"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
    </button>
  );
}

